/**
 * @file Pagination controls component
 * @description Reusable Bootstrap 5.3 pagination controls
 * @version 2.0.0
 */

export default {
  name: 'PaginationControls',

  inject: ['editor'],

  computed: {
    /**
     * First item number on current page
     */
    startItem() {
      if (this.editor.totalItems === 0) return 0;
      return (this.editor.currentPage - 1) * this.editor.pageSize + 1;
    },

    /**
     * Last item number on current page
     */
    endItem() {
      return Math.min(
        this.editor.currentPage * this.editor.pageSize,
        this.editor.totalItems
      );
    },

    /**
     * Visible page numbers with ellipsis for large page counts
     * Shows max 7 page numbers with current page centered
     */
    visiblePages() {
      const delta = 2;
      const range = [];

      for (let i = 1; i <= this.editor.totalPages; i++) {
        if (
          i === 1 ||
          i === this.editor.totalPages ||
          (i >= this.editor.currentPage - delta &&
            i <= this.editor.currentPage + delta)
        ) {
          range.push(i);
        }
      }

      const result = [];
      let prev = 0;
      for (const i of range) {
        if (prev && i - prev > 1) {
          result.push('...');
        }
        result.push(i);
        prev = i;
      }
      return result;
    },
  },

  methods: {
    /**
     * Navigate to a specific page
     * @param {number} page - Page number to navigate to
     */
    goToPage(page) {
      if (
        typeof page === 'number' &&
        page >= 1 &&
        page <= this.editor.totalPages
      ) {
        this.editor.handlePageChange(page);
      }
    },
  },

  template: `
    <nav v-if="editor.totalPages > 1" aria-label="Pagination" class="mt-3">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="text-muted small">
          Showing {{ startItem }} to {{ endItem }} of {{ editor.totalItems }} items
        </div>

        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: editor.currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(1)" title="First">
              <i class="bi bi-chevron-double-left"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: editor.currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(editor.currentPage - 1)" title="Previous">
              <i class="bi bi-chevron-left"></i>
            </a>
          </li>

          <template v-for="page in visiblePages" :key="page">
            <li v-if="page === '...'" class="page-item disabled">
              <span class="page-link">...</span>
            </li>
            <li v-else class="page-item" :class="{ active: page === editor.currentPage }">
              <a class="page-link" href="#" @click.prevent="goToPage(page)">
                {{ page }}
              </a>
            </li>
          </template>

          <li class="page-item" :class="{ disabled: editor.currentPage === editor.totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(editor.currentPage + 1)" title="Next">
              <i class="bi bi-chevron-right"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: editor.currentPage === editor.totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(editor.totalPages)" title="Last">
              <i class="bi bi-chevron-double-right"></i>
            </a>
          </li>
        </ul>
      </div>
    </nav>
  `,
};
